import { useMemo } from 'react'
import { 
  hasPermission, 
  hasAnyPermission, 
  hasAllPermissions, 
  isAdminRole, 
  isManagerRole, 
  isCashierRole, 
  isBaristaRole,
  isBranchRestricted,
  canAccessAllBranches,
  getRoleDisplayName
} from '../config/roles'

/**
 * Custom hook for role-based permissions
 * @param {Object} user - Current user object with role property
 * @returns {Object} - Permission checking functions and user role information
 */
export const usePermissions = (user) => {
  const userRole = user?.role

  const permissions = useMemo(() => {
    if (!userRole) {
      return {
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,
        isAdmin: false,
        isManager: false,
        isCashier: false,
        isBarista: false,
        isBranchRestricted: false,
        canAccessAllBranches: false,
        roleDisplayName: 'Unknown',
        canCreate: () => false,
        canEdit: () => false,
        canDelete: () => false,
        canView: () => false,
        logBranchAccess: () => {},
        getBranchQuery: () => ({})
      }
    }

    return {
      // Permission checking functions
      hasPermission: (permission) => hasPermission(userRole, permission),
      hasAnyPermission: (permissionList) => hasAnyPermission(userRole, permissionList),
      hasAllPermissions: (permissionList) => hasAllPermissions(userRole, permissionList),

      // Role checking
      isAdmin: isAdminRole(userRole),
      isManager: isManagerRole(userRole),
      isCashier: isCashierRole(userRole),
      isBarista: isBaristaRole(userRole),
      isBranchRestricted: isBranchRestricted(userRole),
      canAccessAllBranches: canAccessAllBranches(userRole),
      
      // Role display
      roleDisplayName: getRoleDisplayName(userRole),

      // Convenience functions for common CRUD operations
      canCreate: (entity) => {
        const createPermissions = {
          'products': 'products.create',
          'customers': 'customers.create',
          'suppliers': 'suppliers.create',
          'users': 'users.create',
          'branches': 'branches.create',
          'purchase_orders': 'purchase_orders.create',
          'grn': 'grn.create',
          'promotions': 'promotions.create',
          'tables': 'tables.create'
        }
        const permission = createPermissions[entity]
        return permission ? hasPermission(userRole, permission) : false
      },

      canEdit: (entity) => {
        const editPermissions = {
          'products': 'products.edit',
          'customers': 'customers.edit',
          'suppliers': 'suppliers.edit',
          'users': 'users.edit',
          'branches': 'branches.edit',
          'purchase_orders': 'purchase_orders.edit',
          'grn': 'grn.edit',
          'promotions': 'promotions.edit',
          'tables': 'tables.edit'
        }
        const permission = editPermissions[entity]
        return permission ? hasPermission(userRole, permission) : false
      },

      canDelete: (entity) => {
        const deletePermissions = {
          'products': 'products.delete',
          'customers': 'customers.delete',
          'suppliers': 'suppliers.delete',
          'users': 'users.delete',
          'branches': 'branches.delete',
          'purchase_orders': 'purchase_orders.delete',
          'grn': 'grn.delete',
          'promotions': 'promotions.delete',
          'tables': 'tables.delete'
        }
        const permission = deletePermissions[entity]
        return permission ? hasPermission(userRole, permission) : false
      },

      canView: (entity) => {
        const viewPermissions = {
          'products': 'products.view',
          'customers': ['customers.view_all', 'customers.view_branch'],
          'suppliers': 'suppliers.view',
          'users': 'users.view',
          'branches': 'branches.view',
          'purchase_orders': 'purchase_orders.view',
          'grn': 'grn.view',
          'promotions': 'promotions.view',
          'reports': 'reports.view',
          'tables': 'tables.view',
          'dashboard': 'dashboard.view'
        }
        const permission = viewPermissions[entity]
        if (Array.isArray(permission)) {
          return hasAnyPermission(userRole, permission)
        }
        return permission ? hasPermission(userRole, permission) : false
      },

      // Branch-aware logging function for managers
      logBranchAccess: (entity, action = 'view') => {
        if (isManagerRole(userRole) && isBranchRestricted(userRole)) {
          console.log(`🏢 [MANAGER ACCESS LOG] ${action.toUpperCase()} ${entity}`)
          console.log(`   ├── Manager: ${user?.username || user?.email || 'Unknown'}`)
          console.log(`   ├── Branch ID: ${user?.branchId || 'Not Set'}`)
          console.log(`   ├── Role: ${userRole}`)
          console.log(`   ├── Entity: ${entity}`)
          console.log(`   ├── Action: ${action}`)
          console.log(`   └── Timestamp: ${new Date().toLocaleString()}`)
        }
      },

      // Get branch-filtered query params for managers
      getBranchQuery: (entity) => {
        const query = {}
        
        if (isBranchRestricted(userRole) && user?.branchId) {
          query.branchId = user.branchId
          
          // Log the query for managers
          if (isManagerRole(userRole)) {
            console.log(`🔍 [MANAGER QUERY FILTER] ${entity} filtered by branch ${user.branchId}`)
          }
        }
        
        return query
      }
    }
  }, [userRole, user])

  return permissions
}

/**
 * Hook for checking route access based on user role
 * @param {Object} user - Current user object
 * @param {Array} requiredPermissions - Array of required permissions
 * @param {Array} allowedRoles - Array of allowed roles
 * @returns {boolean} - Whether user has access to the route
 */
export const useRouteAccess = (user, requiredPermissions = [], allowedRoles = []) => {
  return useMemo(() => {
    if (!user?.role) return false

    // Check if role is in allowed roles list
    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      return false
    }

    // Check if user has required permissions
    if (requiredPermissions.length > 0) {
      return hasAnyPermission(user.role, requiredPermissions)
    }

    return true
  }, [user, requiredPermissions, allowedRoles])
}

/**
 * Hook for branch-aware data queries with logging
 * Filters data based on user's branch restrictions and logs access
 * @param {Object} user - Current user object
 * @param {string} selectedBranchId - Currently selected branch ID
 * @param {string} entity - Entity type being queried (for logging)
 * @returns {Object} - Query parameters and branch information
 */
export const useBranchAwareQuery = (user, selectedBranchId, entity = 'data') => {
  return useMemo(() => {
    const userRole = user?.role
    
    if (!userRole) {
      return { branchId: null, isRestricted: false }
    }

    const isRestricted = isBranchRestricted(userRole)
    const canAccessAll = canAccessAllBranches(userRole)
    const effectiveBranchId = isRestricted ? (user.branchId || selectedBranchId) : selectedBranchId

    // Log branch restriction activity for managers
    if (isRestricted && userRole === 'manager') {
      console.log(`🏢 [MANAGER BRANCH RESTRICTION] Manager "${user.username || user.email || 'Unknown'}" accessing ${entity}`)
      console.log(`   ├── User Branch ID: ${user.branchId}`)
      console.log(`   ├── Selected Branch ID: ${selectedBranchId}`)
      console.log(`   ├── Effective Branch ID: ${effectiveBranchId}`)
      console.log(`   ├── Access Restricted: ${isRestricted}`)
      console.log(`   └── Entity: ${entity}`)
      
      if (user.branchId && selectedBranchId && user.branchId !== selectedBranchId) {
        console.warn(`⚠️ [BRANCH MISMATCH] Manager trying to access branch ${selectedBranchId} but restricted to ${user.branchId}`)
      }
    }

    return {
      branchId: effectiveBranchId,
      isRestricted,
      canAccessAll,
      shouldFilterByBranch: isRestricted || (!canAccessAll && selectedBranchId),
      // Additional logging information
      logging: {
        userRole,
        userName: user?.username || user?.email || 'Unknown',
        userBranchId: user?.branchId,
        selectedBranchId,
        entity,
        timestamp: new Date().toISOString()
      }
    }
  }, [user, selectedBranchId, entity])
}
